import { Link } from "react-router-dom";

/**
 * The single source of truth for wording shared by every ODIN legal document.
 *
 * Terms, Privacy and the seven pages under /legal MUST render the same version
 * line, the same contact paragraph and the same description of how a bot reaches
 * a customer's server. They previously each carried their own copy, which drifted
 * — two different "draft for review" notes and two different contact paragraphs.
 * Importing from here is what makes "the wording is the same" a fact rather than
 * an intention: change it once and every document changes with it.
 *
 * These documents are counsel-reviewed and in force. Nothing here may reintroduce
 * a "draft", "to be confirmed" or "before launch" hedge — the service is live and
 * a customer reading a term must be able to rely on it.
 */

export const OWNER = "OKMedia";
export const COUNTRY = "Norway";
export const LEGAL_EMAIL = "post@okmedia.live";

/** Bumped whenever any legal document changes materially. */
export const LEGAL_VERSION = "1.0";

/** The date this version of the documents took effect. */
export const LEGAL_EFFECTIVE_DATE = "30 July 2026";

/**
 * The canonical description of how a bot reaches a customer's Discord server.
 *
 * ODIN's own managed all-in-one bot is the default and only option on Free, Core
 * and Pro. Bringing your own bot identity is a Dedicated-plan capability, gated
 * server-side by `requireByoBotPlan` in the control API and by the
 * `bot.runtime.dedicated` entitlement. Marketing copy, onboarding and these legal
 * documents must all describe it this way; if the gate changes, this string and
 * the gate change together.
 */
export const BOT_MODEL_SENTENCE =
  "ODIN connects to your Discord server using its own managed all-in-one bot, which is added for you through Discord's official authorization — you never create, paste or hold a bot token. Bringing your own bot identity instead is available only on the Dedicated plan.";

/**
 * The canonical list of sub-processors, shared by /privacy and /legal/subprocessors.
 *
 * These two pages used to carry hand-maintained lists that disagreed: /privacy
 * named Stripe, Anthropic and n8n; /legal/subprocessors named none of them and
 * instead claimed "card processing, PayPal and (where available) Vipps MobilePay"
 * plus an unnamed email-delivery provider. Under GDPR Article 28 a controller has
 * to be able to tell customers who actually processes their data, so two lists
 * that disagree is not a cosmetic problem — it makes both of them unreliable.
 *
 * Only providers that are ACTUALLY in use belong here. Stripe is the sole payment
 * provider; PayPal and Vipps MobilePay exist in the codebase as future provider
 * names only (`BillingProviderName`) and are not enabled, so naming them would
 * have told customers their data goes somewhere it does not. There is likewise no
 * transactional-email provider configured yet, so none is listed.
 *
 * Adding a provider is a legal change, not a copy change: add it here (both pages
 * update together), bump LEGAL_VERSION and LEGAL_EFFECTIVE_DATE, and publish
 * before it starts processing.
 */
export interface Subprocessor {
  /** The provider's name, as customers would recognise it. */
  readonly name: string;
  /** What it does for ODIN, in plain language. */
  readonly role: string;
}

export const SUBPROCESSORS: readonly Subprocessor[] = [
  { name: "Railway", role: "application, background workers and database/cache hosting." },
  { name: "Cloudflare", role: "CDN, DNS, TLS, static site delivery and abuse protection (Turnstile)." },
  { name: "Discord", role: "the platform ODIN manages, and a sign-in option." },
  { name: "Google", role: "an optional sign-in option." },
  { name: "Anthropic", role: "the AI model powering BALDER's assistance; see the AI Transparency page for what is sent and retained." },
  { name: "Stripe", role: "billing and payment processing for paid plans. ODIN never stores full card details." },
  { name: "n8n", role: "automation workflows you choose to connect." },
];

/**
 * The sub-processor list itself. Rendered from SUBPROCESSORS on both pages so the
 * two cannot drift apart again — they are the same component, not two copies of
 * the same intention.
 */
export function SubprocessorList(): JSX.Element {
  return (
    <ul>
      {SUBPROCESSORS.map((p) => (
        <li key={p.name}>
          <strong>{p.name}</strong> — {p.role}
        </li>
      ))}
    </ul>
  );
}

/**
 * The version/effective-date note rendered at the top of every legal document.
 * Identical on all of them by construction.
 */
export function LegalMeta(): JSX.Element {
  return (
    <p className="mkt-note" role="note">
      This document is current and in force. Version {LEGAL_VERSION} · effective {LEGAL_EFFECTIVE_DATE}.
      It describes how ODIN actually operates and is not legal advice. If we change it in a way that
      materially affects you, we publish the updated version here before it takes effect.
    </p>
  );
}

/** The contact/ownership paragraph closing every legal document. */
export function LegalContact(): JSX.Element {
  return (
    <p>
      {OWNER} is the sole owner and operator of ODIN, established in {COUNTRY}. ODIN is independently
      operated; third-party services that support it remain independent providers under their own terms.
      Questions about this document? Email <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a> or use the{" "}
      <Link to="/contact">contact page</Link>.
    </p>
  );
}
